import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { borderRadius } from 'styled-system';

import { CourseDownloadableMaterials, VideoOverlay } from 'Components/PaaS/Classroom';
import getConfig from 'Util/Config';
import sanitizeVideoTime from 'Util/videoProgress';
import ArticleBox from 'Containers/Classroom/ArticleBox';
import { ClassroomLayoutView } from 'Routes/Classroom/Components/ClassroomLayout';
import VideoAnalyticListener from 'Containers/Classroom/VideoAnalyticListener';
import FlutterVideoEventHandler from 'Containers/Classroom/FlutterVideoEventHandler';
import { withOffset } from 'Context/LayoutContext';
import { VIDEO_VARIANTS } from 'Components/PaaS/Classroom/VideoOverlay/constant';
import VideoEndingOverlay from 'Containers/VideoEndingOverlay';

import { ClassroomNavigation } from '../../../Domain/Classroom';
import { Box } from '../../Base';
import Quiz from '../../Quiz/Quiz';
import { QUIZ_VARIANTS } from '../../Quiz/constant';

import QuizSimpleBox from './QuizSimpleBox';
import VideoBox from './VideoBox';
import VideoSlide from './VideoSlide';
import MindstampVideoBox from './MindstampVideoBox';
import LearnCraftVideoBox from './LearnCraftVideoBox';
import LiveCoding from './LiveCoding';
import SkooldioVideoBox from './SkooldioVideoBox';
import NoteBoard from './NoteBoard';
import PdfBox from './PdfBox';
import {
  MATERIAL_TYPE,
  SUBSECTION_TYPE,
  CONTENT_DATA,
  EXAM_TAG_CATEGORY,
  VIDEO_PROVIDER,
} from './constant';
import Assignments from './Assignment';

const Container = styled((props) => <Box px={4} py={5} mx="auto" my={0} {...props} />)`
  position: relative;
  width: ${({ width }) => width ?? '100%'};
  max-width: 100%;
  & img {
    max-width: 100%;
  }
  @media screen and (min-width: ${(props) => props.theme.breakpoints[0]}) {
    max-width: 90%;
  }
  ${borderRadius}
`;

const FixedExamContainer = styled((props) => <Box px={4} py={5} mx="auto" my={0} {...props} />)`
  position: relative;
  height: 100%;
  max-height: ${({ theme }) => `calc(100% - ${theme?.space?.CLASSROOM_FOOTER_HEIGHT}px)`};
  background: white;
  max-width: 100%;
  & img {
    max-width: 100%;
  }
  @media screen and (min-width: ${(props) => props.theme.breakpoints[0]}) {
    max-width: 90%;
  }
  ${borderRadius}
`;

const FluidExamContainer = styled(Box)`
  position: relative;
  height: 100%;
  /* background: white; */
  max-height: ${({ theme }) => `calc(100% - ${theme?.space?.CLASSROOM_FOOTER_HEIGHT}px)`};
`;

const ArticleBoard = styled(Box)`
  ${borderRadius}
`;

/**
 * For Anywhere Project
 * according to FullscreenLayout.js
 * 64 offset from header height + device offset + 20 from content designed.
 * 91 from bottom offset px
 *
 * For Platform as a Services
 * 100px from maximum height of subsection title
 */

const PdfBoxContainer = styled(Container)`
  ${({ theme, uiVariant }) =>
    uiVariant !== 'PAAS'
      ? css`
          height: 100vh;
          padding: ${64 + theme.space.APP_TOP_OFFSET + 20}px 0 91px 0;
        `
      : css`
          height: calc(
            100vh - ${theme.space.CLASSROOM_NAVBAR_HEIGHT}px -
              ${theme.space.CLASSROOM_FOOTER_HEIGHT}px - 100px
          );
          padding: 8px 0px;
        `}
`;

const ExamContainer = ({ variant, children }) => {
  switch (variant) {
    case QUIZ_VARIANTS.GRADED_EXAM:
      return (
        <FixedExamContainer width={['95%', null, null, '100ch']} borderRadius="12px">
          {children}
        </FixedExamContainer>
      );
    default:
      return <FluidExamContainer>{children}</FluidExamContainer>;
  }
};

const getExamVariant = (tags = []) => {
  // Exam type have only 1 tag
  const examTypeTags = tags?.find((tag) => tag?.category === EXAM_TAG_CATEGORY.EXAM_TYPE);
  const examType = examTypeTags?.slug?.toLowerCase();

  switch (examType) {
    case QUIZ_VARIANTS.GRADED_EXAM.toLowerCase(): {
      return QUIZ_VARIANTS.GRADED_EXAM;
    }
    case QUIZ_VARIANTS.OFFICIAL_EXAM.toLowerCase(): {
      return QUIZ_VARIANTS.OFFICIAL_EXAM;
    }
    default: {
      return QUIZ_VARIANTS.DEFAULT;
    }
  }
};

class Content extends Component {
  static propTypes = {
    type: PropTypes.oneOf(Object.values(MATERIAL_TYPE)),
    subsectionType: PropTypes.oneOf(Object.values(SUBSECTION_TYPE)),
    courseId: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    secureContent: PropTypes.shape({
      video: PropTypes.func,
      live: PropTypes.func,
      assets: PropTypes.func,
    }),
    onUpdateSubSectionData: PropTypes.func,
    onCompleted: PropTypes.func,
    isResuming: PropTypes.bool,
    subSectionStatus: PropTypes.shape({
      videoProgress: PropTypes.shape({}),
    }),
    checkInvalidDevice: PropTypes.func,
    onUpdateClassroomTime: PropTypes.func,
    isAppStateInactive: PropTypes.bool,
    onVideoError: PropTypes.func,
    delaySetCurrentTime: PropTypes.number,
    videoOrigin: PropTypes.string,
    downloadUrl: PropTypes.string,
    onDownloadClick: PropTypes.func,
    liveCodingProps: PropTypes.object,
  };
  static defaultProps = {
    type: MATERIAL_TYPE.ARTICLE,
    secureContent: {
      video: () => {},
      live: () => {},
      assets: () => {},
    },
    onUpdateSubSectionData: () => {},
    onCompleted: () => {},
    checkInvalidDevice: () => {},
    onUpdateClassroomTime: () => {},
    onVideoError: () => {},
    subSectionStatus: {},
    onDownloadClick: () => {},
  };

  handleLiveCodeModuleLoaded = (LiveCodeOptions) => {
    let LiveCodeApp;
    if (window.SkooldioLivecoding) {
      LiveCodeApp = window.SkooldioLivecoding.new({
        selector: '#liveCodingApp',
        options: LiveCodeOptions,
      });
    }
    if (LiveCodeApp) {
      LiveCodeApp.render();
    } else {
      console.error('SkooldioLivecoding instance is missing.');
    }
  };

  handleUpdateVideoTime = (time) => {
    const videoTime = sanitizeVideoTime(time, { assetDuration: this.props.value?.duration });
    if (videoTime === null) return;
    this.props.onUpdateSubSectionData({ dataType: CONTENT_DATA.VIDEOTIME, value: videoTime });
  };
  renderContent = () => {
    const {
      classroom: { autoNextSubsectionOnVideoEnd, enableLighthouseUserId },
      videoPlayer: { showVideoOverlayOnEnd },
    } = getConfig();
    const enableAutoNextSubsectionOnVideoEnd = autoNextSubsectionOnVideoEnd?.enable ?? false;
    const {
      courseId,
      revision,
      contentTitle,
      title,
      type,
      value,
      secureContent,
      onCompleted,
      videoOverlayDisplayName,
      videoOverlayPhoneNumber,
      onVideoOverlayCorrupted,
      subSectionStatus,
      isResuming,
      checkInvalidDevice,
      onUpdateClassroomTime,
      isAppStateInactive,
      onVideoError,
      delaySetCurrentTime,
      videoOrigin,
      userProfileData,
      getDownloadlink,
      onExitClassroom,
      isLastSubsectionInCourse,
      isLearnCraftNewLayout,
      logSubsectionVideoOnVideoPlayerError,
    } = this.props;

    /*
      In case enable showVideoOverlayOnEnd
      Enable auto next subsection, Last Subsection shouldn't show overlay
      Disable auto next subsection, Subsection should show video overlay UI in previous version
    */
    let shouldShowVideoOverlay = showVideoOverlayOnEnd;
    if (enableAutoNextSubsectionOnVideoEnd && showVideoOverlayOnEnd) {
      if (!isLastSubsectionInCourse) shouldShowVideoOverlay = true;
      else shouldShowVideoOverlay = false;
    }

    const videoProps = {
      onCompleted,
      onUpdateVideoTime: this.handleUpdateVideoTime,
      onUpdateClassroomTime,
      onOverlayCorrupted: onVideoOverlayCorrupted,
      onVideoError,
      checkInvalidDevice,
      delaySetCurrentTime,
      overlayDisplayName: videoOverlayDisplayName,
      overlayPhoneNumber: videoOverlayPhoneNumber,
      videoProgress: subSectionStatus?.videoProgress ?? {},
      isResuming,
      isAppStateInactive,
      videoOrigin,
      userProfileData,
      enableLighthouseUserId,
      showVideoOverlayOnEnd: shouldShowVideoOverlay,
      secureLive: secureContent.live,
      secureVideo: secureContent.video,
      renderCustomVideoOverlay: ({
        videoSize,
        videoState,
        VIDEO_STATE,
        closeOverlay,
        isShowOverlay,
        pause,
        play,
        replay,
      }) => {
        return (
          <ClassroomNavigation>
            {({
              goToNextSubsection,
              nextSubsection,
              currentSubsection,
              isLastSubsectionInCourse,
            }) => (
              <VideoOverlay
                isShowOverlay={isShowOverlay}
                isLastSubsectionInCourse={isLastSubsectionInCourse}
                onReplayClick={replay}
                onNextSubsectionClick={goToNextSubsection}
                onCancelClick={closeOverlay}
                onResumeClick={play}
                videoSize={videoSize}
                active={
                  videoState === VIDEO_STATE.READY ||
                  videoState === VIDEO_STATE.ENDED ||
                  videoState === VIDEO_STATE.PAUSED
                }
                currentSubsectionTitle={currentSubsection?.title}
                nextSubsectionTitle={videoState === VIDEO_STATE.ENDED && nextSubsection?.title}
                variant={
                  videoState === VIDEO_STATE.ENDED ? VIDEO_VARIANTS.ENDED : VIDEO_VARIANTS.PAUSED
                }
              />
            )}
          </ClassroomNavigation>
        );
      },
    };

    const videoFullLayoutProps = {
      ...videoProps,
      showVideoOverlayOnEnd,
      /**@type {RenderCustomVideoOverlayFunc}*/ renderCustomVideoOverlay: ({
        isShowOverlay,
        closeOverlay,
        replay,
      }) => {
        return (
          isShowOverlay && (
            <VideoEndingOverlay
              onCloseOverlay={closeOverlay}
              onReplayClick={replay}
              courseId={courseId}
            />
          )
        );
      },
    };

    switch (type) {
      case MATERIAL_TYPE.VIDEO_SLIDE: {
        const { slideTimeList: slideAsset, ...videoAsset } = value;
        const {
          classroom: { enableFlutterVideoEventHandler },
        } = getConfig();
        return (
          <>
            <VideoAnalyticListener />
            {enableFlutterVideoEventHandler && <FlutterVideoEventHandler />}
            <VideoSlide
              videoAsset={videoAsset}
              slideAsset={slideAsset}
              assetReferId={this.props.referId}
              onGetSecureAssets={secureContent.assets}
              videoProps={videoProps}
            />
          </>
        );
      }
      case MATERIAL_TYPE.VIDEO: {
        const {
          classroom: { layoutType, enableFlutterVideoEventHandler },
        } = getConfig();

        // check if it's skooldio classroom
        if (layoutType === ClassroomLayoutView.VARIANTS.FIXED) {
          return (
            <>
              <VideoAnalyticListener />
              {enableFlutterVideoEventHandler && <FlutterVideoEventHandler />}
              <SkooldioVideoBox
                isVideoJsControl
                title={title}
                type={type}
                value={value}
                {...videoProps}
              />
            </>
          );
        }

        // layoutType === FULL
        if (value.provider === VIDEO_PROVIDER.MINDSTAMP) {
          return <MindstampVideoBox title={title} value={value} {...videoFullLayoutProps} />;
        }
        if (value.provider === VIDEO_PROVIDER.LEARNCRAFT) {
          return (
            <LearnCraftVideoBox
              title={title}
              type={type}
              value={value}
              revision={revision}
              courseId={courseId}
              isLearnCraftNewLayout={isLearnCraftNewLayout}
              logSubsectionVideoOnVideoPlayerError={logSubsectionVideoOnVideoPlayerError}
              {...videoFullLayoutProps}
            />
          );
        }
        return (
          <>
            <VideoAnalyticListener />
            <VideoBox
              title={title}
              type={type}
              value={value}
              logSubsectionVideoOnVideoPlayerError={logSubsectionVideoOnVideoPlayerError}
              {...videoFullLayoutProps}
            />
          </>
        );
      }
      case MATERIAL_TYPE.YOUTUBE:
      case MATERIAL_TYPE.LIVE_VIDEO: {
        const {
          classroom: { layoutType, enableFlutterVideoEventHandler },
        } = getConfig();

        // check if it's skooldio classroom
        if (layoutType === ClassroomLayoutView.VARIANTS.FIXED) {
          return (
            <>
              <VideoAnalyticListener />
              {enableFlutterVideoEventHandler && <FlutterVideoEventHandler />}
              <SkooldioVideoBox
                isVideoJsControl
                title={title}
                type={type}
                value={value}
                {...videoProps}
              />
            </>
          );
        }

        return (
          <>
            <VideoAnalyticListener />
            <VideoBox title={title} type={type} value={value} {...videoFullLayoutProps} />
          </>
        );
      }
      case MATERIAL_TYPE.QUIZ: {
        const { id, title, type, description } = this.props;
        const { examId, maxAttempts, timeLimit, fullScore, passingScore, rawTags, resultOptions } =
          value;
        const {
          classroom: { quiz: quizConfig = {} },
        } = getConfig();
        const { showTimer = false } = quizConfig;
        const quiz = {
          courseAssetId: id,
          title,
          description,
          examId,
          maxAttempts,
          fullScore,
          passingScore,
          timeLimit: showTimer ? timeLimit : 0,
          resultOptions,
          type,
        };

        const examVariant = getExamVariant(rawTags);

        return (
          <ClassroomNavigation>
            {({ goToNextSubsection, isLastSubsectionInCourse }) => {
              return (
                <ExamContainer variant={examVariant}>
                  <Quiz
                    variant={examVariant}
                    quiz={quiz}
                    onEndQuiz={goToNextSubsection}
                    onCompleted={onCompleted}
                    isLastSubsectionInCourse={isLastSubsectionInCourse}
                    onExitClassroom={onExitClassroom}
                  />
                </ExamContainer>
              );
            }}
          </ClassroomNavigation>
        );
      }
      case MATERIAL_TYPE.QUIZ_SIMPLE: {
        const {
          id,
          onUpdateQuizSimpleResult,
          referId: quizId,
          userAssetDocument,
          onUpdateUserAsset,
        } = this.props;
        return (
          <Box>
            <QuizSimpleBox
              key={`${type}-${id}`}
              id={id}
              {...value}
              onCompleted={onCompleted}
              onUpdateQuizSimpleResult={(index, isCorrect, answeredChoiceIndex) =>
                onUpdateQuizSimpleResult(index, isCorrect, answeredChoiceIndex, quizId)
              }
              userAssetDocument={userAssetDocument}
              onUpdateUserAssetData={onUpdateUserAsset}
            />
          </Box>
        );
      }

      case MATERIAL_TYPE.MATERIAL: {
        const { onDownloadClick, downloadUrl, subsectionType } = this.props;
        const materialContainerStyledProps =
          subsectionType === SUBSECTION_TYPE.ARTICLE
            ? { px: 0, width: ['95%', null, null, '100ch'] }
            : {};
        return (
          <Container {...materialContainerStyledProps}>
            <CourseDownloadableMaterials
              items={value}
              onDownloadClick={onDownloadClick}
              downloadUrl={downloadUrl}
            />
          </Container>
        );
      }
      case MATERIAL_TYPE.LIVECODING: {
        const {
          liveCodingProps: { hasArticle },
          referId: assetReferId,
        } = this.props;
        return (
          <Container width={hasArticle ? [1, 1, 1, 3 / 5] : undefined}>
            <LiveCoding value={value} onCompleted={onCompleted} assetReferId={assetReferId} />
          </Container>
        );
      }
      case MATERIAL_TYPE.ARTICLE: {
        const { subsectionType } = this.props;
        const isVideoSubsection = subsectionType === SUBSECTION_TYPE.VIDEO;
        const isVideoSlideSubsection = subsectionType === SUBSECTION_TYPE.VIDEO_SLIDE;
        if (subsectionType === SUBSECTION_TYPE.LIVECODING) {
          return (
            <Container width={[1, 1, 1, 2 / 5]} pt={[null, null, null, '76px']} borderRadius="12px">
              <ArticleBoard p={20} bg="#F9F9F9" borderRadius="12px">
                <ArticleBox rawContent={value.htmlContent} onCompleted={onCompleted} />
              </ArticleBoard>
            </Container>
          );
        }
        return (
          <Container
            width={['95%', null, null, '100ch']}
            bg="white"
            px={['20px', '40px']}
            py={['20px', '48px']}
            mt={isVideoSubsection || isVideoSlideSubsection ? 6 : 0}
            borderRadius="12px"
          >
            <ArticleBox rawContent={value.htmlContent} onCompleted={onCompleted} />
          </Container>
        );
      }
      case MATERIAL_TYPE.NOTEBOARD: {
        const {
          referId: assetReferId,
          userAssetDocument,
          userAssetDataError,
          isUserAssetDataLoading,
          onUpdateUserAsset,
        } = this.props;
        return (
          <NoteBoard
            value={value}
            onCompleted={onCompleted}
            assetReferId={assetReferId}
            userAssetDocument={userAssetDocument}
            userAssetDataError={userAssetDataError}
            isUserAssetDataLoading={isUserAssetDataLoading}
            onUpdateUserAsset={onUpdateUserAsset}
          />
        );
      }
      case MATERIAL_TYPE.EBOOK: {
        const { uiVariant } = getConfig();
        return (
          <PdfBoxContainer uiVariant={uiVariant}>
            <PdfBox
              assetReferId={this.props.referId}
              data={value.data}
              handleGetFileUrl={getDownloadlink}
              onCompleted={onCompleted}
              checkInvalidDevice={checkInvalidDevice}
            />
          </PdfBoxContainer>
        );
      }

      case MATERIAL_TYPE.ASSIGNMENT: {
        const { classroomCourseId, referId } = this.props;
        return (
          <Container
            width={['95%', null, null, '100ch']}
            bg="white"
            px={['20px', '40px']}
            py={['20px', '48px']}
            borderRadius="12px"
          >
            <Assignments
              contentTitle={contentTitle}
              content={value}
              classroomId={classroomCourseId}
              courseAssetId={referId}
              onCompleted={onCompleted}
            />
          </Container>
        );
      }

      default:
        return <ArticleBox rawContent={value.htmlContent} onCompleted={onCompleted} />;
    }
  };
  render() {
    const { type } = this.props;
    const ChildComponent = this.renderContent();
    switch (type) {
      case MATERIAL_TYPE.VIDEO:
      case MATERIAL_TYPE.QUIZ:
      case MATERIAL_TYPE.YOUTUBE:
      case MATERIAL_TYPE.LIVE_VIDEO:
      case MATERIAL_TYPE.VIDEO_SLIDE:
      case MATERIAL_TYPE.ARTICLE:
      case MATERIAL_TYPE.LIVECODING:
      case MATERIAL_TYPE.MATERIAL:
      case MATERIAL_TYPE.EBOOK:
        return ChildComponent;
      default:
        return <Container>{ChildComponent}</Container>;
    }
  }
}
export default withOffset(Content);

/**
 * @typedef RenderVideoOverlayParams
 * @property {boolean} isShowOverlay
 * @property {function} closeOverlay
 * @property {function} replay
 */

/** @typedef {function(RenderVideoOverlayParams): JSX.Element} RenderCustomVideoOverlayFunc */
